// Public SSR destination page — /destino/:slug (single URL for ES/EN/PT-BR).
// SEO-first: dense content, JSON-LD (TouristDestination, ItemList, FAQPage),
// hreflang tags, hero image, and hotel list rendered server-side.
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  getDestinationBySlug,
  listDestinationHotels,
  listPublishedDestinations,
} from "@/lib/destinations.functions";
import { getHost } from "@/lib/host";
import { localeFromHost, canonicalOriginFromHost } from "@/lib/locale-from-host";
import { buildHreflangLinks } from "@/lib/hreflang";
import { RouteErrorBoundary } from "@/lib/route-boundaries";
import {
  pickLocaleContent,
  CATEGORY_LABELS,
  RELEVANT_INFO_KEYS,
  RELEVANT_INFO_LABELS,
} from "@/lib/destinations-shared";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Star, MapPin } from "lucide-react";

type LoaderData = {
  destination: Awaited<ReturnType<typeof getDestinationBySlug>>;
  hotels: Awaited<ReturnType<typeof listDestinationHotels>>;
  others: Awaited<ReturnType<typeof listPublishedDestinations>>;
  locale: ReturnType<typeof localeFromHost>;
  origin: string;
};

export const Route = createFileRoute("/destino/$slug")({
  loader: async ({ params }): Promise<LoaderData> => {
    const host = getHost();
    const locale = localeFromHost(host);
    const origin = canonicalOriginFromHost(host);
    const dest = await getDestinationBySlug({ data: { slug: params.slug } });
    if (!dest) {
      return { destination: null, hotels: [], others: [], locale, origin };
    }
    const [hotels, others] = await Promise.all([
      listDestinationHotels({
        data: {
          destinationId: dest.id,
          iterpecDestinationId: dest.iterpec_destination_id,
          limit: 12,
        },
      }),
      listPublishedDestinations({
        data: { limit: 8, excludeSlug: dest.slug },
      }),
    ]);
    return { destination: dest, hotels, others, locale, origin };
  },
  head: ({ loaderData }) => {
    const d = loaderData?.destination;
    if (!d) {
      return { meta: [{ title: "Destino no encontrado | Fora da Rota" }, { name: "robots", content: "noindex" }] };
    }
    const locale = loaderData.locale;
    const origin = loaderData.origin;
    const c = pickLocaleContent(d, locale);
    const title = `${d.name}${d.state ? ", " + d.state : ""} | Fora da Rota`.slice(0, 60);
    const desc = (c.meta_description || c.slogan || c.intro).slice(0, 160);
    const canonical = `${origin}/destino/${d.slug}`;
    const hero = d.hero_image_url;
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "website" },
        { property: "og:url", content: canonical },
        ...(hero ? [{ property: "og:image", content: hero }, { name: "twitter:image", content: hero }] : []),
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [
        { rel: "canonical", href: canonical },
        ...buildHreflangLinks(`/destino/${d.slug}`),
      ],
    };
  },
  component: DestinationPage,
  errorComponent: RouteErrorBoundary,
});

function DestinationPage() {
  const { destination, hotels, others, locale, origin } = Route.useLoaderData();
  // Visits tracked server-side inside getDestinationBySlug loader.


  if (!destination) {
    return (
      <>
        <Navbar />
        <main className="container mx-auto px-4 py-24 text-center">
          <h1 className="text-3xl font-bold">Destino no encontrado</h1>
          <p className="text-muted-foreground mt-2">
            <Link to="/" className="underline">Volver al inicio</Link>
          </p>
        </main>
        <Footer />
      </>
    );
  }

  const c = pickLocaleContent(destination, locale);
  const canonical = `${origin}/destino/${destination.slug}`;
  const hotelListItems = hotels.map((h, i) => ({
    "@type": "ListItem",
    position: i + 1,
    url: `${origin}/hotel/${h.slug}`,
    name: h.name,
  }));

  const faqItems = RELEVANT_INFO_KEYS
    .map((k) => {
      const val = destination.relevant_info?.[k];
      const answer = (locale === "pt-BR" ? val?.pt : locale === "es" ? val?.es : val?.en) ?? "";
      if (!answer) return null;
      return {
        "@type": "Question",
        name: RELEVANT_INFO_LABELS[k][locale === "pt-BR" ? "pt-BR" : locale === "es" ? "es" : "en"],
        acceptedAnswer: { "@type": "Answer", text: answer },
      };
    })
    .filter(Boolean);

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "TouristDestination",
        name: destination.name,
        description: c.intro?.slice(0, 500) ?? c.slogan,
        url: canonical,
        image: destination.hero_image_url,
        ...(destination.latitude && destination.longitude
          ? { geo: { "@type": "GeoCoordinates", latitude: destination.latitude, longitude: destination.longitude } }
          : {}),
        ...(destination.country
          ? { containedInPlace: { "@type": "Country", name: destination.country } }
          : {}),
      },
      ...(hotelListItems.length > 0
        ? [{ "@type": "ItemList", name: `Hoteles en ${destination.name}`, itemListElement: hotelListItems }]
        : []),
      ...(faqItems.length > 0
        ? [{ "@type": "FAQPage", mainEntity: faqItems }]
        : []),
    ],
  };

  return (
    <>
      <Navbar />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      {/* Hero */}
      <section className="relative h-[420px] w-full overflow-hidden">
        {destination.hero_image_url && (
          <img
            src={destination.hero_image_url}
            alt={destination.name}
            className="absolute inset-0 h-full w-full object-cover"
            fetchPriority="high"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        <div className="relative z-10 container mx-auto px-4 h-full flex flex-col justify-end pb-10 text-white">
          <div className="flex flex-wrap gap-2 mb-3">
            {(destination.categories ?? []).map((cat) => (
              <Badge key={cat} variant="secondary" className="bg-white/20 text-white border-white/30">
                {(CATEGORY_LABELS as any)[cat]?.[locale === "pt-BR" ? "pt-BR" : locale === "es" ? "es" : "en"] ?? cat}
              </Badge>
            ))}
          </div>
          <h1 className="text-4xl md:text-5xl font-bold">{destination.name}</h1>
          <p className="text-lg mt-2 opacity-90">{[destination.state, destination.country].filter(Boolean).join(", ")}</p>
          {c.slogan && <p className="mt-3 text-xl italic opacity-95">{c.slogan}</p>}
        </div>
      </section>

      <main className="container mx-auto px-4 py-10 grid gap-8 lg:grid-cols-[1fr_320px]">
        <div className="space-y-10">
          {/* Intro */}
          <section>
            <h2 className="text-2xl font-bold mb-3">Sobre {destination.name}</h2>
            <div className="prose prose-neutral max-w-none whitespace-pre-line">{c.intro}</div>
          </section>

          {/* Known for */}
          {c.known_for.length > 0 && (
            <section>
              <h2 className="text-2xl font-bold mb-3">
                {locale === "pt-BR" ? "Conhecido por" : locale === "es" ? "Conocido por" : "Known for"}
              </h2>
              <ul className="grid gap-2 md:grid-cols-2">
                {c.known_for.map((item, i) => (
                  <li key={i} className="flex gap-2"><span className="text-primary">•</span>{item}</li>
                ))}
              </ul>
            </section>
          )}

          {/* Hotels */}
          {hotels.length > 0 && (
            <section>
              <h2 className="text-2xl font-bold mb-4">
                {locale === "pt-BR" ? "Hospedagem em " : locale === "es" ? "Alojamiento en " : "Stay in "}
                {destination.name}
              </h2>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {hotels.map((h) => (
                  <Link key={h.hotel_id} to="/hotel/$slug" params={{ slug: h.slug } as any}>
                    <Card className="overflow-hidden group hover:shadow-lg transition-shadow">
                      <div className="aspect-video overflow-hidden bg-muted">
                        {h.thumb_url && (
                          <img src={h.thumb_url} alt={h.name} loading="lazy"
                            className="h-full w-full object-cover group-hover:scale-105 transition-transform" />
                        )}
                      </div>
                      <div className="p-3">
                        <div className="flex items-center gap-1 text-amber-500 text-xs mb-1">
                          {Array.from({ length: h.star_rating ?? 0 }).map((_, i) => <Star key={i} className="h-3 w-3 fill-current" />)}
                          {h.featured && <Badge variant="default" className="ml-1 text-[10px] h-4">★ Recomendado</Badge>}
                        </div>
                        <h3 className="font-semibold text-sm line-clamp-2">{h.name}</h3>
                        {h.city && <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                          <MapPin className="h-3 w-3" />{h.city}
                        </p>}
                      </div>
                    </Card>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* Getting around */}
          {c.getting_around && (
            <section>
              <h2 className="text-2xl font-bold mb-3">
                {locale === "pt-BR" ? "Como se locomover" : locale === "es" ? "Cómo desplazarse" : "Getting around"}
              </h2>
              <div className="whitespace-pre-line">{c.getting_around}</div>
            </section>
          )}

          {/* Secret tips */}
          {c.secret_tips && (
            <section>
              <h2 className="text-2xl font-bold mb-3">
                {locale === "pt-BR" ? "Dicas de quem mora aqui" : locale === "es" ? "Tips locales" : "Local tips"}
              </h2>
              <div className="whitespace-pre-line">{c.secret_tips}</div>
            </section>
          )}
        </div>

        {/* Sidebar */}
        <aside className="space-y-6">
          <Card className="p-4">
            <h3 className="font-bold mb-3">
              {locale === "pt-BR" ? "Informações relevantes" : locale === "es" ? "Información relevante" : "Key info"}
            </h3>
            <table className="w-full text-sm">
              <tbody>
                {RELEVANT_INFO_KEYS.map((k) => {
                  const answer = c.relevant_info?.[k];
                  if (!answer) return null;
                  return (
                    <tr key={k} className="border-b last:border-0">
                      <th className="text-left py-2 pr-2 font-medium text-muted-foreground align-top">
                        {RELEVANT_INFO_LABELS[k][locale === "pt-BR" ? "pt-BR" : locale === "es" ? "es" : "en"]}
                      </th>
                      <td className="py-2">{answer}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>

          {others.length > 0 && (
            <Card className="p-4">
              <h3 className="font-bold mb-3">
                {locale === "pt-BR" ? "Outros destinos" : locale === "es" ? "Otros destinos" : "Other destinations"}
              </h3>
              <ul className="space-y-2">
                {others.map((o: any) => (
                  <li key={o.slug}>
                    <Link to="/destino/$slug" params={{ slug: o.slug } as any} className="text-sm hover:underline flex items-center gap-2">
                      <MapPin className="h-3 w-3 text-muted-foreground" />
                      {o.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </aside>
      </main>
      <Footer />
    </>
  );
}
